import type { Point } from 'klinecharts'
import type { OverlayStyleOverride } from '../types/persistence'

/* ------------------------------------------------------------------ */
/*  Overlay sync event                                                 */
/* ------------------------------------------------------------------ */

export interface OverlaySyncEvent {
  action: 'create' | 'update' | 'remove'
  syncId: string
  name?: string
  points?: Array<Partial<Point>>
  /** Per-drawing style override, carried on create/update so style edits
   *  persist + broadcast to peer charts. All optional — legacy events omit it. */
  styles?: OverlayStyleOverride
  /** Visibility / lock state, carried on create/update. Optional for back-compat. */
  visible?: boolean
  lock?: boolean
  /**
   * Per-instance NON-geometric state — a Text tool's string, a note body, an
   * Elliott wave label, a Fib level-set choice. Opaque to every layer of the
   * round-trip; only the owning overlay template interprets it. Absent on
   * legacy drawings and on tools that carry no extra state.
   */
  extendData?: unknown
}

/* ------------------------------------------------------------------ */
/*  Overlay sync group                                                 */
/* ------------------------------------------------------------------ */

export interface OverlaySyncGroup {
  subscribe: (chartId: string, ticker: string, handler: (event: OverlaySyncEvent) => void) => void
  unsubscribe: (chartId: string) => void
  updateTicker: (chartId: string, ticker: string) => void
  publish: (sourceChartId: string, ticker: string, event: OverlaySyncEvent) => void
}

/**
 * Creates a sync group for broadcasting overlay create/update/remove events
 * across charts that display the same ticker.
 */
export function createOverlaySyncGroup(): OverlaySyncGroup {
  const entries = new Map<string, { ticker: string; handler: (event: OverlaySyncEvent) => void }>()
  let publishing = false

  return {
    subscribe(chartId, ticker, handler) {
      entries.set(chartId, { ticker, handler })
    },

    unsubscribe(chartId) {
      entries.delete(chartId)
    },

    updateTicker(chartId, ticker) {
      const entry = entries.get(chartId)
      if (entry) entry.ticker = ticker
    },

    publish(sourceChartId, ticker, event) {
      if (publishing) return
      publishing = true
      entries.forEach((entry, id) => {
        if (id !== sourceChartId && entry.ticker === ticker) {
          entry.handler(event)
        }
      })
      publishing = false
    },
  }
}
